import React, { useMemo } from 'react';
import SortableTable from '../SortableTable';
import InfoTooltip from '../ui/InfoTooltip';
import { safeArray, safeNum, formatInt, TEMPLATE_FALLBACK_LABEL } from '../../utils/gpsFormUtils';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR');
};

const CHOICE_TYPES = new Set(['choice', 'multichoice']);


const SUBTOTAL_TOOLTIP =
  'Nombre de formulaires remplis (soumissions). Un même ticket peut avoir soumis plusieurs formulaires : ce total compte les soumissions, pas les tickets.';

const ANSWERS_COUNT_TOOLTIP =
  'Nombre de questions distinctes auxquelles ce formulaire a répondu (mesure la complétude du formulaire, pas le nombre de valeurs saisies).';

// Normalize an answer value to classify Oui/Non-like pastilles.
const normalize = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const isYesValue = (value) => ['oui', 'yes', 'true', 'vrai'].includes(normalize(value));
const isNoValue = (value) => ['non', 'no', 'false', 'faux'].includes(normalize(value));

// Pastille for choice/multichoice answer values: green Oui / red Non / neutral else.
const AnswerPastille = ({ value }) => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (text === '') return <span className="text-gray-400 dark:text-gray-500">—</span>;

  let tone =
    'text-gray-700 bg-gray-100 border border-gray-200 dark:text-gray-200 dark:bg-gray-700/40 dark:border-gray-600';
  if (isYesValue(text)) {
    tone =
      'text-emerald-700 bg-emerald-600/10 border border-emerald-400/30 dark:text-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-400/30';
  } else if (isNoValue(text)) {
    tone =
      'text-red-700 bg-red-600/10 border border-red-400/30 dark:text-red-300 dark:bg-red-500/10 dark:border-red-400/30';
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11.5px] font-medium ${tone}`}
    >
      <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {text}
    </span>
  );
};

// Form-grain detail table: one row per filled form on a ticket. A "Voir le
// contexte" button opens the per-form Q/A modal; column filters are enabled.
const GPSFormDetailTable = ({
  answers,
  loading,
  projectKey,
  startDate,
  endDate,
  selectedQuestionLabel,
  questionType,
  onClearQuestionFilter,
  onOpenForm,
}) => {
  const rows = safeArray(answers?.rows);
  const total = safeNum(answers?.total, rows.length);

  const questionExplored = Boolean(selectedQuestionLabel);

  const columns = useMemo(() => {
    const base = [
      { key: 'issueKey', label: 'Ticket', filterable: true },
      {
        key: 'formTemplateName',
        label: 'Formulaire',
        filterable: true,
        // §13 — greyed fallback when the template name is empty.
        render: (row) => {
          const name = typeof row?.formTemplateName === 'string' ? row.formTemplateName.trim() : '';
          return name !== '' ? (
            name
          ) : (
            <span className="italic text-gray-400 dark:text-gray-500">{TEMPLATE_FALLBACK_LABEL}</span>
          );
        },
        rawValue: (row) => {
          const name = typeof row?.formTemplateName === 'string' ? row.formTemplateName.trim() : '';
          return name !== '' ? name : TEMPLATE_FALLBACK_LABEL;
        },
      },
      { key: 'interventionPoint', label: 'Magasin', filterable: true },
      { key: 'shopsign', label: 'Enseigne', filterable: true },
      {
        key: 'answeredAt',
        label: 'Date',
        filterable: true,
        render: (row) => formatDate(row?.answeredAt),
        sortFunction: (a, b, direction) => {
          const ta = new Date(a?.answeredAt || 0).getTime() || 0;
          const tb = new Date(b?.answeredAt || 0).getTime() || 0;
          return direction === 'ascending' ? ta - tb : tb - ta;
        },
        rawValue: (row) => row?.answeredAt ?? '',
      },
    ];

    // Réponse column only when a question is explored.
    if (questionExplored) {
      base.push({
        key: 'answerValue',
        label: 'Réponse',
        filterable: true,
        render: (row) =>
          CHOICE_TYPES.has(questionType) ? (
            <AnswerPastille value={row?.answerValue} />
          ) : (
            <span className="block max-w-[260px] truncate" title={row?.answerValue ?? ''}>
              {typeof row?.answerValue === 'string' && row.answerValue.trim() !== '' ? (
                row.answerValue
              ) : (
                <span className="text-gray-400 dark:text-gray-500">—</span>
              )}
            </span>
          ),
        rawValue: (row) => row?.answerValue ?? '',
      });
    }

    base.push(
      {
        key: 'answersCount',
        label: (
          <span className="inline-flex items-center gap-1">
            Nb réponses
            <InfoTooltip text={ANSWERS_COUNT_TOOLTIP} />
          </span>
        ),
        filterable: false,
        render: (row) => <span className="block text-right tabular-nums">{formatInt(row?.answersCount)}</span>,
        rawValue: (row) => safeNum(row?.answersCount),
      },
      {
        key: 'action',
        label: '',
        filterable: false,
        render: (row) => (
          <button
            type="button"
            onClick={() => onOpenForm?.(row)}
            className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-400"
          >
            Voir les réponses
          </button>
        ),
        rawValue: () => '',
      }
    );

    return base;
  }, [onOpenForm, questionExplored, questionType]);

  const exportFilename = `formulaires-${projectKey || 'projet'}-${startDate || ''}_${endDate || ''}.csv`;

  // Footer count: "Total : N formulaires" + InfoTooltip. Per-value filtering is
  // handled by the Réponse column's built-in column filter (filterable: true).
  const countLabel = (
    <span className="inline-flex items-center gap-1">
      <span>Total : {formatInt(total)} formulaires</span>
      <InfoTooltip text={SUBTOTAL_TOOLTIP} />
    </span>
  );

  return (
    <div id="form-detail-table" className="bg-white dark:bg-[#1e1e1e] p-6 rounded-xl border border-gray-200 dark:border-gray-900 shadow-sm">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Détail des réponses</h3>
        {selectedQuestionLabel && (
          <button
            type="button"
            onClick={() => onClearQuestionFilter?.()}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-800"
            aria-label="Retirer le filtre de question"
          >
            <span className="truncate max-w-[220px]">Filtré : {selectedQuestionLabel}</span>
            <span aria-hidden="true">✕</span>
          </button>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse space-y-2" data-testid="detail-table-loading">
          <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded" />
          <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded" />
          <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded" />
        </div>
      ) : (
        <SortableTable
          data={rows}
          columns={columns}
          globalSearch
          exportable
          exportFilename={exportFilename}
          initialSortBy={{ key: 'answeredAt', direction: 'descending' }}
          countLabel={countLabel}
          emptyMessage="Aucun formulaire pour la période et les filtres sélectionnés."
        />
      )}
    </div>
  );
};

export default GPSFormDetailTable;
